// pages/SteeringPage.js
// Cross-section progress dashboard for the Steering role. Loads pre-computed
// JSON from blob (via /api/diSteering/{type}) for Manager vs Partner views.
// Mimics the user's existing HTML dashboard: filters, doughnut charts per
// section-type, overall progress bar, and side-by-side Incomplete / Complete
// tables with drill-down.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip as ChartTooltip,
  Legend,
} from 'chart.js';
import { Doughnut } from 'react-chartjs-2';
import TopBar from '../components/TopBar';
import LastRefreshed from '../components/LastRefreshed';
import Modal from '../components/Modal';
import { useAuth } from '../auth/AuthContext';
import { useSectionData } from '../hooks/useSectionData';
import './SteeringPage.css';

// True when rich-text HTML carries any visible text (ignoring tags / &nbsp;).
function htmlHasContent(html) {
  return String(html || '').replace(/<[^>]*>/g, '').replace(/&nbsp;/gi, ' ').trim() !== '';
}

// Section labels / CTAs / reminder phrasing come from the server (api/shared/sectionMeta.js):
// diSteering sends `sectionMeta` ({ key: { label, cta, url } }) and a `Phrase` on every
// PROGRESSBYTYPE entry, so this page keeps no copy of them. The modal preview body is only a
// preview — the server re-derives it when it actually sends.

// Canonical app URL — mirrors the server default for DI_PUBLIC_URL.
const DI_APP_URL = 'https://dataintegrity.botfield.ai';

// Optional shared "From" address the operator can send as (toggle in the modal).
// The server only honors this exact address (not arbitrary spoofing).
const KEITH_FROM_EMAIL = 'KeithBotfield@bmss.com';

// Build the same URL the server's emailTemplate will write into the link — the
// app routes on /section/:period/:sectionKey, so the period segment is required.
function clientUrlFor(sectionMeta, sectionKey, diPublicUrl, period) {
  const meta = sectionMeta?.[sectionKey];
  const base = (diPublicUrl || DI_APP_URL).replace(/\/$/, '');
  if (!meta) return base;
  if (meta.url) return meta.url;
  return period ? `${base}/section/${period}/${sectionKey}` : `${base}/section/${sectionKey}`;
}

function firstNameFromClient(displayName, email) {
  if (displayName && typeof displayName === 'string') {
    const t = displayName.trim();
    if (t) {
      if (t.includes(',')) {
        const after = t.split(',')[1] || '';
        const first = after.trim().split(/\s+/)[0];
        if (first) return first;
      }
      const first = t.split(/\s+/)[0];
      if (first) return first;
    }
  }
  if (email && email.includes('@')) {
    const local = email.split('@')[0];
    if (local) return local.charAt(0).toUpperCase() + local.slice(1);
  }
  return 'there';
}

// Computes the bullet phrases the same way the server will. Pure for preview.
function previewBulletsFor(staff, selectedSectionKeys, diPublicUrl, period, sectionMeta) {
  const haveFilter = selectedSectionKeys.length > 0;
  const out = [];
  for (const p of (staff.PROGRESSBYTYPE || [])) {
    if (haveFilter && !selectedSectionKeys.includes(p.LookupType)) continue;
    const items = Number(p.Items || 0);
    const completed = Number(p.Completed || 0);
    if (items <= 0 || completed >= items) continue;
    const meta = sectionMeta?.[p.LookupType];
    out.push({
      sectionKey: p.LookupType,
      label:  meta?.label || p.LookupType,
      phrase: p.Phrase || `${p.LookupType} still needs attention`,
      url:    clientUrlFor(sectionMeta, p.LookupType, diPublicUrl, period),
      cta:    meta?.cta || 'Open Data Integrity',
    });
  }
  return out;
}

ChartJS.register(ArcElement, ChartTooltip, Legend);

// ── helpers ──────────────────────────────────────────────────────────────────

function parseLastAccessed(v) {
  if (v == null || v === '') return null;
  // Accept ISO strings as-is.
  if (typeof v === 'string' && isNaN(Number(v))) {
    const d = new Date(v);
    return isNaN(d.getTime()) ? null : d;
  }
  const n = Number(v);
  if (!isFinite(n)) return null;
  let ms;
  if (n > 1e17 && n < 1e19) ms = Math.floor(n / 10000) - 62135596800000; // .NET ticks
  else if (n > 1e15)         ms = Math.floor(n / 1000);                   // microseconds
  else if (n > 1e12)         ms = n;                                      // ms
  else if (n > 1e9)          ms = n * 1000;                               // seconds
  else if (n > 30000 && n < 60000)
    ms = Math.round(n * 86400000) - (25569 * 86400000);                   // Excel serial
  else ms = n;
  const d = new Date(ms);
  return isNaN(d.getTime()) ? null : d;
}

function fmtDate(v) {
  const d = parseLastAccessed(v);
  return d ? d.toLocaleDateString() : '';
}

function statusClass(completed, items) {
  if (items === 0) return '';
  if (completed === items) return 'sp-row-green';
  if (completed === 0) return 'sp-row-red';
  return 'sp-row-yellow';
}

// ── row sub-components ──────────────────────────────────────────────────────

function StaffRow({ staff, activeFilters, expanded, onToggleExpand, tableKind }) {
  const rowClass = statusClass(staff.COMPLETED, staff.ITEMS);
  const types = Array.isArray(staff.PROGRESSBYTYPE) ? staff.PROGRESSBYTYPE : [];
  const shown = activeFilters.size
    ? types.filter(p => activeFilters.has(p.LookupType))
    : types;

  function mailto(e) {
    e.stopPropagation();
    if (tableKind !== 'incomplete' || !staff.EMAIL) return;
    const incomplete = types
      .filter(p => p.Completed !== p.Items)
      .map(p => `\t*${p.Type}*:    ${p.Completed}/${p.Items} of items completed`)
      .join('\n');
    const subject = encodeURIComponent('Reminder about Incomplete Data Integrity Action Items');
    const body    = encodeURIComponent(`Incomplete action items:\n${incomplete || 'None'}`);
    window.location.href = `mailto:${staff.EMAIL}?subject=${subject}&body=${body}`;
  }

  return (
    <>
      <tr className={`${rowClass} ${expanded ? 'sp-row-expanded' : ''}`} onClick={mailto}>
        <td className="sp-expand-cell"
            onClick={e => { e.stopPropagation(); onToggleExpand(); }}>
          <i className={`fas fa-chevron-${expanded ? 'down' : 'right'}`} />
        </td>
        <td className="sp-staff">{staff.STAFFNAME}</td>
        <td className="sp-center">{staff.COMPLETED}</td>
        <td className="sp-center">{staff.ITEMS}</td>
        <td className="sp-center">{fmtDate(staff.LASTACCESSED)}</td>
      </tr>
      {expanded && (
        <tr className="sp-subrow">
          <td colSpan={5}>
            <table className="sp-subtable">
              <thead>
                <tr>
                  <th>Type</th>
                  <th>Completed</th>
                  <th>Items</th>
                </tr>
              </thead>
              <tbody>
                {shown.length === 0 ? (
                  <tr><td colSpan={3}>No data</td></tr>
                ) : shown.map(p => (
                  <tr key={p.LookupType + p.Type} className={statusClass(p.Completed || 0, p.Items || 0)}>
                    <td>{p.Type}</td>
                    <td className="sp-center">{p.Completed || 0}</td>
                    <td className="sp-center">{p.Items || 0}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </td>
        </tr>
      )}
    </>
  );
}

const STAFF_PAGE_SIZE = 10;

function StaffTable({ rows, activeFilters, expanded, onToggleExpand, tableKind, loading }) {
  const [search, setSearch] = useState('');
  const [sortKey, setSortKey] = useState('STAFFNAME');
  const [sortDir, setSortDir] = useState('asc');
  const [page, setPage] = useState(1);

  const filtered = useMemo(() => {
    const s = search.toLowerCase();
    const arr = s
      ? rows.filter(r =>
          (r.STAFFNAME || '').toLowerCase().includes(s) ||
          (r.EMAIL || '').toLowerCase().includes(s))
      : rows.slice();
    arr.sort((a, b) => {
      const av = a[sortKey] ?? '';
      const bv = b[sortKey] ?? '';
      if (typeof av === 'number' && typeof bv === 'number') {
        return sortDir === 'asc' ? av - bv : bv - av;
      }
      return sortDir === 'asc'
        ? String(av).localeCompare(String(bv))
        : String(bv).localeCompare(String(av));
    });
    return arr;
  }, [rows, search, sortKey, sortDir]);

  function toggleSort(key) {
    if (sortKey === key) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortKey(key); setSortDir('asc'); }
    setPage(1);
  }

  function sortIcon(key) {
    if (sortKey !== key) return null;
    return <i className={`fas fa-caret-${sortDir === 'asc' ? 'up' : 'down'}`} />;
  }

  const totalPages = Math.max(1, Math.ceil(filtered.length / STAFF_PAGE_SIZE));
  const safePage   = Math.min(page, totalPages);
  const paged      = filtered.slice((safePage - 1) * STAFF_PAGE_SIZE, safePage * STAFF_PAGE_SIZE);

  return (
    <div className="sp-table-wrap">
      <input
        className="sp-search"
        placeholder="Search staff or email…"
        value={search}
        onChange={e => { setSearch(e.target.value); setPage(1); }}
      />
      <table className="sp-table">
        <thead>
          <tr>
            <th className="sp-expand-col" />
            <th onClick={() => toggleSort('STAFFNAME')}>Staff {sortIcon('STAFFNAME')}</th>
            <th onClick={() => toggleSort('COMPLETED')}>Completed {sortIcon('COMPLETED')}</th>
            <th onClick={() => toggleSort('ITEMS')}>Total {sortIcon('ITEMS')}</th>
            <th onClick={() => toggleSort('LASTACCESSED')}>Last Accessed {sortIcon('LASTACCESSED')}</th>
          </tr>
        </thead>
        <tbody>
          {loading ? (
            <tr><td colSpan={5} className="sp-empty"><i className="fas fa-spinner fa-spin" aria-hidden="true" /> Loading…</td></tr>
          ) : filtered.length === 0 ? (
            <tr><td colSpan={5} className="sp-empty">No rows</td></tr>
          ) : paged.map(staff => (
            <StaffRow
              key={staff.EMAIL || staff.STAFFNAME}
              staff={staff}
              activeFilters={activeFilters}
              expanded={expanded.has(staff.EMAIL || staff.STAFFNAME)}
              onToggleExpand={() => onToggleExpand(staff.EMAIL || staff.STAFFNAME)}
              tableKind={tableKind}
            />
          ))}
        </tbody>
      </table>

      {filtered.length > STAFF_PAGE_SIZE && (
        <div className="sp-pagination">
          <button disabled={safePage <= 1} onClick={() => setPage(1)}>«</button>
          <button disabled={safePage <= 1} onClick={() => setPage(p => p - 1)}>‹</button>
          <span className="sp-page-info">Page {safePage} of {totalPages} ({filtered.length} rows)</span>
          <button disabled={safePage >= totalPages} onClick={() => setPage(p => p + 1)}>›</button>
          <button disabled={safePage >= totalPages} onClick={() => setPage(totalPages)}>»</button>
        </div>
      )}
    </div>
  );
}

// Editable reminder prose (the greeting, per-person item list, and "Thanks! Todd"
// sign-off are always auto-generated; the operator can tailor the intro/closing
// per send — e.g. set the real deadline).
const DEFAULT_REMINDER_SUBJECT = 'Reminder: Data Integrity items remaining';
// Prose is HTML now (rich-text editor). Defaults are simple HTML.
const DEFAULT_REMINDER_INTRO = 'You still have outstanding items for this round of data integrity:';
const DEFAULT_REMINDER_CLOSING = "Your leadership in this effort will have a tremendous impact on our firm's ability to operate efficiently. Please complete this by the end of this week.";
const DEFAULT_REMINDER_SIGNATURE = 'Thanks!<br>Todd';

// "Initial Email" mode — an intro/kickoff message that EXPLAINS the review
// rather than nagging. Operator can edit any of it in the compose box before
// sending. Signature is shared with the reminder mode.
const DEFAULT_INITIAL_SUBJECT = 'Semi-annual Data Integrity review — items assigned to you';
const DEFAULT_INITIAL_INTRO = "We've begun this cycle's data integrity review. As part of it, the items below have been assigned to you to review and verify — this note explains what's involved and what we'll need from you:";
const DEFAULT_INITIAL_CLOSING = "Keeping this data accurate has a real impact on how efficiently the firm runs. Please work through your items during this review window; we'll follow up if anything is still outstanding.";

// Per-mode default prose bundle, applied when the operator flips the toggle.
const EMAIL_MODE_DEFAULTS = {
  reminder: { subject: DEFAULT_REMINDER_SUBJECT, intro: DEFAULT_REMINDER_INTRO, closing: DEFAULT_REMINDER_CLOSING },
  initial:  { subject: DEFAULT_INITIAL_SUBJECT,  intro: DEFAULT_INITIAL_INTRO,  closing: DEFAULT_INITIAL_CLOSING },
};

// Lightweight rich-text field — a contentEditable box with a Bold/Italic/
// Underline toolbar (execCommand). No external editor dependency. Emits HTML;
// the server sanitizes to an allowlist of formatting tags before sending.
function RichTextField({ value, onChange, disabled, minRows = 2, placeholder }) {
  const ref = useRef(null);

  // Set the DOM html only when it diverges from `value` (initial mount + external
  // resets). Skipping the no-op assignment keeps the caret from jumping on type.
  useEffect(() => {
    if (ref.current && ref.current.innerHTML !== (value || '')) {
      ref.current.innerHTML = value || '';
    }
  }, [value]);

  const exec = (cmd) => {
    document.execCommand(cmd, false);
    if (ref.current) onChange(ref.current.innerHTML);
    if (ref.current) ref.current.focus();
  };

  return (
    <div className={`sp-rte ${disabled ? 'sp-rte--disabled' : ''}`}>
      <div className="sp-rte-toolbar">
        {[['bold', 'B', 'strong'], ['italic', 'I', 'em'], ['underline', 'U', 'u']].map(([cmd, glyph, Tag]) => (
          <button key={cmd} type="button" className="sp-rte-btn" title={cmd[0].toUpperCase() + cmd.slice(1)}
            disabled={disabled}
            onMouseDown={e => e.preventDefault()}   /* keep the editor's selection */
            onClick={() => exec(cmd)}>
            <Tag>{glyph}</Tag>
          </button>
        ))}
      </div>
      <div
        ref={ref}
        className="sp-rte-editor"
        contentEditable={!disabled}
        role="textbox"
        aria-multiline="true"
        data-placeholder={placeholder || ''}
        style={{ minHeight: `${minRows * 1.5}em` }}
        suppressContentEditableWarning
        onInput={e => onChange(e.currentTarget.innerHTML)}
      />
    </div>
  );
}

// ── Reminder modal ───────────────────────────────────────────────────────────
//
// "Send Sample to Me" sends ONE example email to the operator's inbox (optional
// sanity check). "Send to All" can be used without testing first, but still
// requires the operator to type SEND to confirm the real send.
function ReminderModal({
  open, onClose, role, typeAgg, officeFilter, levelFilter, departmentFilter,
  staffPool, callerEmail, diPublicUrl, period, sectionMeta,
  sendingTest, testResult, realResult,
  confirmOpen, confirmText, setConfirmOpen, setConfirmText,
  subjectText, introText, closingText, signatureText,
  setSubjectText, setIntroText, setClosingText, setSignatureText,
  includedSections, setIncludedSections, excludedEmails, setExcludedEmails,
  fromKeith, setFromKeith,
  emailMode, onModeChange,
  onSendTest, onSendReal,
}) {
  // Inputs lock once the real-send confirm row is open, so what was tested is
  // exactly what goes out.
  const proseLocked = confirmOpen;

  // Sections available for this role, each with an included/excluded state.
  // Included = the section (and its data) is part of the email.
  const allSections = typeAgg
    .map(([k, v]) => ({ key: k, label: v.label }))
    .sort((a, b) => String(a.label || '').localeCompare(String(b.label || '')));
  const includedKeys = allSections.map(s => s.key).filter(k => includedSections.has(k));
  const allIncluded = allSections.length > 0 && includedKeys.length === allSections.length;
  const selectedLabels = allSections.filter(s => includedSections.has(s.key)).map(s => s.label);

  // Candidates = staff with incomplete work in an INCLUDED section (staffPool
  // already reflects the office/level filters). The operator can then exclude
  // individuals; finalRecipients is who actually receives the email.
  const eligibleRecipients = (staffPool || []).filter(s =>
    (s.PROGRESSBYTYPE || []).some(p =>
      includedSections.has(p.LookupType) && (p.Completed || 0) < (p.Items || 0))
  ).sort((a, b) => String(a.STAFFNAME || '').localeCompare(String(b.STAFFNAME || '')));
  const finalRecipients = eligibleRecipients.filter(s => !excludedEmails.has(s.EMAIL));
  const recipientCount = finalRecipients.length;

  // Preview reflects the first person who will actually receive it + the
  // included sections only.
  const previewStaff = finalRecipients[0];
  const previewBullets = previewStaff ? previewBulletsFor(previewStaff, includedKeys, diPublicUrl, period, sectionMeta) : [];
  const previewFirstName = previewStaff
    ? firstNameFromClient(previewStaff.STAFFNAME, previewStaff.EMAIL)
    : 'there';
  const previewSubject = previewBullets.length === 1
    ? `Reminder: Complete your ${previewBullets[0].label}`
    : 'Reminder: Data Integrity items remaining';

  const toggleSection = (k) => setIncludedSections(prev => {
    const next = new Set(prev);
    if (next.has(k)) next.delete(k); else next.add(k);
    return next;
  });
  const toggleRecipient = (em) => setExcludedEmails(prev => {
    const next = new Set(prev);
    if (next.has(em)) next.delete(em); else next.add(em);
    return next;
  });

  return (
    <Modal open={open} onClose={onClose} title="Send Email to Incomplete" wide>
      <div className="sp-rm-mode" role="tablist" aria-label="Email type">
        <button
          type="button"
          role="tab"
          aria-selected={emailMode === 'reminder'}
          className={`sp-rm-mode-btn ${emailMode === 'reminder' ? 'active' : ''}`}
          onClick={() => onModeChange && onModeChange('reminder')}
        >
          Reminder
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={emailMode === 'initial'}
          className={`sp-rm-mode-btn ${emailMode === 'initial' ? 'active' : ''}`}
          onClick={() => onModeChange && onModeChange('initial')}
        >
          Initial Email
        </button>
        <span className="sp-rm-mode-hint">
          {emailMode === 'initial'
            ? 'Kickoff message that explains the review (not a reminder).'
            : 'Nudge for people with items still outstanding.'}
        </span>
      </div>

      <div className="sp-rm-meta">
        <dl className="sp-rm-meta-grid">
          <dt>Role:</dt>       <dd>{role === 'manager' ? 'Manager' : 'Partner'}</dd>
          <dt>Sections:</dt>   <dd>{allIncluded ? '(all sections)' : (selectedLabels.join(', ') || '(none selected)')}</dd>
          <dt>Office:</dt>     <dd>{officeFilter || 'All Offices'}</dd>
          <dt>Level:</dt>      <dd>{levelFilter  || 'All Levels'}</dd>
          <dt>Department:</dt> <dd>{departmentFilter || 'All Departments'}</dd>
          <dt>Recipients:</dt> <dd><strong>{recipientCount}</strong> {recipientCount === 1 ? 'person' : 'people'}{excludedEmails.size ? ` (${excludedEmails.size} excluded)` : ''}</dd>
          <dt>From:</dt>
          <dd>
            {fromKeith ? KEITH_FROM_EMAIL : (callerEmail || '(you)')}
            <label className="sp-rm-from-toggle" title={`Send the email from ${KEITH_FROM_EMAIL} instead of you`}>
              <input
                type="checkbox"
                checked={fromKeith}
                disabled={proseLocked}
                onChange={e => setFromKeith(e.target.checked)}
              />
              Send from {KEITH_FROM_EMAIL}
            </label>
          </dd>
        </dl>
      </div>

      <div className="sp-rm-compose">
        <div className="sp-rm-compose-field">
          <span className="sp-rm-compose-label">Subject</span>
          <input className="sp-rm-subject" type="text" value={subjectText}
            disabled={proseLocked}
            onChange={e => setSubjectText(e.target.value)}
            placeholder="Reminder: Data Integrity items remaining" />
        </div>
        <div className="sp-rm-compose-field">
          <span className="sp-rm-compose-label">Intro message</span>
          <RichTextField value={introText} onChange={setIntroText} disabled={proseLocked}
            minRows={2} placeholder="Message shown above each person's incomplete-items list…" />
        </div>
        <div className="sp-rm-compose-field">
          <span className="sp-rm-compose-label">Closing message</span>
          <RichTextField value={closingText} onChange={setClosingText} disabled={proseLocked}
            minRows={3} placeholder="Message shown below the list (e.g. the deadline)…" />
        </div>
        <div className="sp-rm-compose-field">
          <span className="sp-rm-compose-label">Closing line / signature</span>
          <RichTextField value={signatureText} onChange={setSignatureText} disabled={proseLocked}
            minRows={2} placeholder="Thanks! / Todd" />
        </div>
        <div className="sp-rm-compose-hint">
          Select text and use <strong>B</strong> / <em>I</em> / <u>U</u> to format. The greeting and each person&rsquo;s incomplete-items list are added automatically. Re-test after editing.
        </div>
      </div>

      <div className="sp-rm-select">
        <div className="sp-rm-select-head">
          <span className="sp-rm-select-title">Sections ({includedKeys.length}/{allSections.length})</span>
          <span className="sp-rm-select-actions">
            <button type="button" onClick={() => setIncludedSections(new Set(allSections.map(s => s.key)))} disabled={proseLocked}>All</button>
            <button type="button" onClick={() => setIncludedSections(new Set())} disabled={proseLocked}>None</button>
          </span>
        </div>
        <div className="sp-rm-checklist">
          {allSections.map(s => (
            <label key={s.key} className="sp-rm-check">
              <input type="checkbox" checked={includedSections.has(s.key)} disabled={proseLocked}
                onChange={() => toggleSection(s.key)} />
              {s.label}
            </label>
          ))}
        </div>
      </div>

      <div className="sp-rm-select">
        <div className="sp-rm-select-head">
          <span className="sp-rm-select-title">Recipients ({recipientCount}/{eligibleRecipients.length})</span>
          <span className="sp-rm-select-actions">
            <button type="button" onClick={() => setExcludedEmails(new Set())} disabled={proseLocked}>All</button>
            <button type="button" onClick={() => setExcludedEmails(new Set(eligibleRecipients.map(s => s.EMAIL)))} disabled={proseLocked}>None</button>
          </span>
        </div>
        <div className="sp-rm-checklist sp-rm-checklist--recipients">
          {eligibleRecipients.length === 0 ? (
            <div className="sp-rm-empty">No incomplete recipients for the selected sections.</div>
          ) : eligibleRecipients.map(s => (
            <label key={s.EMAIL || s.STAFFNAME} className="sp-rm-check">
              <input type="checkbox" checked={!excludedEmails.has(s.EMAIL)} disabled={proseLocked}
                onChange={() => toggleRecipient(s.EMAIL)} />
              {s.STAFFNAME}{s.STAFFOFFICE ? ` · ${s.STAFFOFFICE}` : ''}
            </label>
          ))}
        </div>
      </div>

      {previewStaff && (
        <div className="sp-rm-preview">
          <div className="sp-rm-preview-label">Preview (rendered for {previewStaff.STAFFNAME})</div>
          <div className="sp-rm-preview-subject">Subject: {subjectText.trim() ? subjectText : previewSubject}</div>
          <div className="sp-rm-preview-body">
            <p>Hi {previewFirstName},</p>
            {htmlHasContent(introText) && <div dangerouslySetInnerHTML={{ __html: introText }} />}
            <ul style={{ paddingLeft: 22, margin: '0 0 12px' }}>
              {previewBullets.map((b, i) => (
                <li key={i} style={{ marginBottom: 6 }}>
                  {b.phrase} &mdash;{' '}
                  <a href={b.url} target="_blank" rel="noreferrer" style={{ color: '#1a3a5c' }}>
                    {b.cta}
                  </a>
                  {b.url && b.url !== '#' && (
                    <div style={{ fontSize: '0.75rem', color: '#888', marginTop: 2, wordBreak: 'break-all' }}>
                      {b.url}
                    </div>
                  )}
                </li>
              ))}
            </ul>
            {htmlHasContent(closingText) && <div dangerouslySetInnerHTML={{ __html: closingText }} />}
            {/* Fixed support line — the server appends this to every send
                (emailTemplate.js). Shown here so the preview matches. */}
            <p style={{ fontSize: '0.92em', color: '#555' }}>
              For any technical questions or trouble please reach out to the{' '}
              <a href="mailto:dataanalyticsteam@bmss.com">Data Team</a>.
            </p>
            {htmlHasContent(signatureText) && (
              <>
                <br />
                <br />
                <div dangerouslySetInnerHTML={{ __html: signatureText }} />
              </>
            )}
          </div>
        </div>
      )}

      {/* Action buttons */}
      <div className="sp-rm-actions">
        <button
          className="sp-rm-btn-test"
          onClick={onSendTest}
          disabled={sendingTest || recipientCount === 0}
        >
          {sendingTest ? 'Sending sample…' : `Send Sample to Me (${callerEmail || 'your inbox'})`}
        </button>

        {!confirmOpen ? (
          <button
            className="sp-rm-btn-real"
            onClick={() => setConfirmOpen(true)}
            disabled={sendingTest || recipientCount === 0}
          >
            <i className="fas fa-exclamation-triangle" /> Send to All {recipientCount} Recipients
          </button>
        ) : null}
      </div>

      {/* Test result banner */}
      {testResult && (testResult.error || testResult.failed > 0) && (
        <div className="sp-rm-test-fail">
          ✗ Test send had problems. Attempted {testResult.attempted ?? 0}, sent {testResult.sent ?? 0}, failed {testResult.failed ?? 0}.
          {testResult.error ? ` Error: ${testResult.error}` : ''}
          {' '}Fix the issue, then re-test before sending for real.
        </div>
      )}

      {/* Typed confirmation row for the real send */}
      {confirmOpen && (
        <div className="sp-rm-confirm-row">
          <span>This will send to <strong>{recipientCount}</strong> real people. Type <strong>SEND</strong> to confirm:</span>
          <input
            type="text"
            value={confirmText}
            onChange={e => setConfirmText(e.target.value)}
            autoFocus
          />
          <button
            className="sp-rm-btn-real"
            onClick={onSendReal}
            disabled={confirmText.trim().toUpperCase() !== 'SEND'}
          >
            Send for Real
          </button>
          <button
            className="sp-rm-btn-test"
            onClick={() => { setConfirmOpen(false); setConfirmText(''); }}
          >
            Cancel
          </button>
        </div>
      )}

      {/* Real-send result */}
      {realResult && (
        <div className={realResult.error || realResult.failed > 0 ? 'sp-rm-test-fail' : 'sp-rm-test-success'} style={{ marginTop: 14 }}>
          <div style={{ marginBottom: 8 }}>
            {realResult.error
              ? `✗ Send failed: ${realResult.error}`
              : `${realResult.failed === 0 ? '✓' : '⚠'} Sent ${realResult.sent} of ${realResult.attempted} (${realResult.failed} failed)`}
          </div>
          {realResult.recipients && realResult.recipients.length > 0 && (
            <details>
              <summary style={{ cursor: 'pointer' }}>Per-recipient detail</summary>
              <table className="sp-rm-result-table" style={{ marginTop: 6 }}>
                <thead>
                  <tr><th>Recipient</th><th>Email</th><th>Status</th><th>Error</th></tr>
                </thead>
                <tbody>
                  {realResult.recipients.map((r, i) => (
                    <tr key={i}>
                      <td>{r.name}</td>
                      <td>{r.email}</td>
                      <td className={`status-${r.status}`}>{r.status}</td>
                      <td>{r.error || ''}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </details>
          )}
        </div>
      )}
    </Modal>
  );
}

// ── Main page ────────────────────────────────────────────────────────────────

// Module-level cache (survives unmount, so revisiting the page is instant) keyed
// by cycle code ('__default__' = newest active). Each cached payload is served
// immediately while a fresh fetch runs in the background — see displayData below.
const steeringCache = new Map();
const cacheKeyFor = (period) => period || '__default__';

export default function SteeringPage() {
  const { email: callerEmail, diPublicUrl } = useAuth();
  const [tab, setTab] = useState('manager');   // 'manager' | 'partner'
  // Concurrent active periods: which one Steering is viewing. null = let the
  // server default to the newest active period (its code comes back in `data`).
  const [selectedPeriod, setSelectedPeriod] = useState(null);

  const [officeFilter, setOfficeFilter] = useState('');
  const [levelFilter, setLevelFilter] = useState('');
  const [departmentFilter, setDepartmentFilter] = useState('');
  const [activeFilters, setActiveFilters] = useState(new Set());
  const [expandedIn, setExpandedIn] = useState(new Set());
  const [expandedDone, setExpandedDone] = useState(new Set());

  // ── Reminder modal state ───────────────────────────────────────────────
  const [reminderOpen, setReminderOpen] = useState(false);
  const [sendingTest, setSendingTest] = useState(false);
  const [testResult, setTestResult] = useState(null);      // null | { ok, attempted, sent, failed, recipients }
  const [realResult, setRealResult] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);    // shows the typed-confirm row
  const [confirmText, setConfirmText] = useState('');
  // Operator-editable subject + intro/closing/signature prose for the reminder.
  const [subjectText, setSubjectText]     = useState(DEFAULT_REMINDER_SUBJECT);
  const [introText, setIntroText]         = useState(DEFAULT_REMINDER_INTRO);
  const [closingText, setClosingText]     = useState(DEFAULT_REMINDER_CLOSING);
  const [signatureText, setSignatureText] = useState(DEFAULT_REMINDER_SIGNATURE);
  // Email mode: 'reminder' (nudge) vs 'initial' (kickoff/explainer). Switching
  // reloads that mode's default subject/intro/closing into the compose fields
  // (the operator can still edit after). Signature is shared.
  const [emailMode, setEmailMode] = useState('reminder');
  const applyEmailMode = useCallback((mode) => {
    const d = EMAIL_MODE_DEFAULTS[mode] || EMAIL_MODE_DEFAULTS.reminder;
    setEmailMode(mode);
    setSubjectText(d.subject);
    setIntroText(d.intro);
    setClosingText(d.closing);
  }, []);
  // Per-send scoping: which sections to include, and which recipients to exclude.
  const [includedSections, setIncludedSections] = useState(new Set());  // section keys
  const [excludedEmails, setExcludedEmails]      = useState(new Set());  // recipient emails
  // When on, the email is sent FROM KEITH_FROM_EMAIL instead of the operator.
  const [fromKeith, setFromKeith] = useState(false);

  // Fetch both tabs once at mount; afterwards the app heartbeat refetches when
  // either role's shared rollup snapshot is rebuilt (at most every 2 min — Steering
  // may lag sign-offs by that much). Tab switching is a state flip, not a refetch —
  // same cached data feeds both.
  const fetcher = useCallback(async () => {
    const qs = selectedPeriod ? `?period=${encodeURIComponent(selectedPeriod)}` : '';
    const [mgr, ptr] = await Promise.all([
      fetch(`/api/diSteering/manager${qs}`, { credentials: 'include', cache: 'no-cache' })
        .then(r => r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`))),
      fetch(`/api/diSteering/partner${qs}`, { credentials: 'include', cache: 'no-cache' })
        .then(r => r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`))),
    ]);
    const result = {
      manager: mgr.rows || [],
      partner: ptr.rows || [],
      // Active periods (for the switcher) + the period actually rolled up.
      periods: mgr.periods || [],
      resolvedPeriod: mgr.period || null,
      // Section labels / CTAs for the reminder preview (server: sectionMeta.clientMeta()).
      sectionMeta: mgr.sectionMeta || {},
      // Which cycle this payload is for — lets the UI tell a fresh result from
      // the previous cycle's stale data during a switch.
      forPeriod: selectedPeriod || null,
    };
    steeringCache.set(cacheKeyFor(selectedPeriod), result);
    return result;
  }, [selectedPeriod]);

  // The cycle whose rollups to watch: the selected one, else the server's default.
  // Read from the per-cycle cache so it's known before the hook's data lands.
  const watchPeriod = selectedPeriod || steeringCache.get(cacheKeyFor(selectedPeriod))?.resolvedPeriod || null;
  const { data, loading, error, lastRefreshedAt, freshness, silentRefetch }
    = useSectionData(fetcher, {
      deps: [selectedPeriod],
      heartbeat: watchPeriod ? {
        datasets: [
          { name: 'steeringRollup', params: { role: 'manager', period: watchPeriod } },
          { name: 'steeringRollup', params: { role: 'partner', period: watchPeriod } },
        ],
      } : null,
    });

  // Show the hook's data only when it matches the selected cycle; otherwise fall
  // back to a cached payload for this cycle (instant on revisit / cycle re-select)
  // while the background fetch — already in flight — refreshes it. Spinner shows
  // only when there's nothing cached to display.
  const cachedForKey = steeringCache.get(cacheKeyFor(selectedPeriod));
  const dataMatches  = data && (selectedPeriod ? data.forPeriod === selectedPeriod : data.forPeriod == null);
  const displayData    = dataMatches ? data : (cachedForKey || null);
  const displayLoading = loading && !displayData;
  // Wrapped in useMemo so the `|| []` fallback doesn't create a fresh array
  // reference on every render — that was busting the three useMemo hooks
  // below (offices / levels / scoped) and tripping the CI lint gate.
  const rawData = useMemo(() => displayData?.[tab] || [], [displayData, tab]);

  // Tab switch resets filters/expansion (data refresh lives in the hook).
  useEffect(() => {
    setActiveFilters(new Set());
    setExpandedIn(new Set());
    setExpandedDone(new Set());
  }, [tab]);

  const offices = useMemo(
    () => [...new Set(rawData.map(s => s.STAFFOFFICE).filter(Boolean))].sort(),
    [rawData]
  );
  const levels = useMemo(
    () => [...new Set(rawData.map(s => s.STAFFLEVEL).filter(Boolean))].sort(),
    [rawData]
  );
  const departments = useMemo(
    () => [...new Set(rawData.map(s => s.STAFFDEPARTMENT).filter(Boolean))].sort(),
    [rawData]
  );

  const scoped = useMemo(() => rawData.filter(s =>
    (!officeFilter     || s.STAFFOFFICE     === officeFilter) &&
    (!levelFilter      || s.STAFFLEVEL      === levelFilter) &&
    (!departmentFilter || s.STAFFDEPARTMENT === departmentFilter)
  ), [rawData, officeFilter, levelFilter, departmentFilter]);

  // Aggregate per section-type for the doughnuts.
  const typeAgg = useMemo(() => {
    const map = {};
    for (const staff of scoped) {
      for (const p of staff.PROGRESSBYTYPE || []) {
        if (!map[p.LookupType]) map[p.LookupType] = { label: p.Type, completed: 0, items: 0 };
        map[p.LookupType].completed += (p.Completed || 0);
        map[p.LookupType].items     += (p.Items || 0);
      }
    }
    return Object.entries(map)
      .sort((a, b) => (a[1].label || '').localeCompare(b[1].label || ''));
  }, [scoped]);

  // Recompute per-staff counts applying the active type filter, then split.
  const { completeRows, incompleteRows, totalCompleted, totalItems } = useMemo(() => {
    const active = activeFilters;
    const mapped = scoped.map(staff => {
      const types = staff.PROGRESSBYTYPE || [];
      const shown = active.size ? types.filter(p => active.has(p.LookupType)) : types;
      const COMPLETED = shown.reduce((s, p) => s + (p.Completed || 0), 0);
      const ITEMS     = shown.reduce((s, p) => s + (p.Items || 0), 0);
      return { ...staff, PROGRESSBYTYPE: types, COMPLETED, ITEMS };
    }).filter(s => s.ITEMS > 0);

    return {
      completeRows:   mapped.filter(s => s.COMPLETED === s.ITEMS),
      incompleteRows: mapped.filter(s => s.COMPLETED !== s.ITEMS),
      totalCompleted: mapped.reduce((sum, s) => sum + s.COMPLETED, 0),
      totalItems:     mapped.reduce((sum, s) => sum + s.ITEMS, 0),
    };
  }, [scoped, activeFilters]);

  const overallPct = totalItems ? Math.round((totalCompleted / totalItems) * 100) : 0;

  function toggleTypeFilter(lookupType) {
    setActiveFilters(prev => {
      const next = new Set(prev);
      if (next.has(lookupType)) next.delete(lookupType); else next.add(lookupType);
      return next;
    });
  }

  function toggleRow(setter) {
    return (key) => setter(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  }

  function openReminderModal() {
    const anyIncomplete = scoped.some(s => (s.PROGRESSBYTYPE || []).some(p => (p.Completed || 0) < (p.Items || 0)));
    if (!anyIncomplete) { alert('No incomplete staff to email.'); return; }
    // Fresh state every open so the "destructive" button is always disabled
    // until a test send succeeds in this session.
    setTestResult(null);
    setRealResult(null);
    setConfirmOpen(false);
    setConfirmText('');
    // Preselect the sections currently filtered on the doughnuts (activeFilters);
    // if none are selected, include every section. Office/Level already flow into
    // the population via `scoped` + the officeFilter/levelFilter we POST.
    setIncludedSections(new Set(activeFilters.size ? activeFilters : typeAgg.map(([k]) => k)));
    setExcludedEmails(new Set());
    setFromKeith(false);
    setReminderOpen(true);
  }

  async function postReminder(testMode) {
    const res = await fetch('/api/diSendReminder', {
      method:  'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({
        role:         tab,
        // Roll up the SAME cycle the operator is viewing — otherwise the send
        // targets a different period's population than the on-screen list they
        // curated/excluded from.
        period:       selectedPeriod || displayData?.resolvedPeriod || undefined,
        sectionKeys:  Array.from(includedSections),
        excludeEmails: Array.from(excludedEmails),
        officeFilter,
        levelFilter,
        departmentFilter,
        testMode,
        fromKeith,
        subjectText,
        introText,
        closingText,
        signatureText,
      }),
    });
    if (!res.ok) {
      let errBody = '';
      try { errBody = (await res.text()).slice(0, 300); } catch (_) { /* ignore */ }
      throw new Error(`HTTP ${res.status}: ${errBody}`);
    }
    return res.json();
  }

  async function handleSendTest() {
    setSendingTest(true);
    try {
      const result = await postReminder(true);
      setTestResult(result);
      if (result && !result.error && (result.failed || 0) === 0 && (result.sent || 0) > 0) {
        const pop = result.population ?? result.sent;
        toast.success(`Sample sent to ${callerEmail} (prefixed [TEST]). A real send would reach ${pop} ${pop === 1 ? 'person' : 'people'}.`);
      }
    } catch (err) {
      setTestResult({ ok: false, attempted: 0, sent: 0, failed: 0, recipients: [], error: err.message });
    } finally {
      setSendingTest(false);
    }
  }

  function handleSendReal() {
    // Not optimistic about the OUTCOME: emails can't be un-sent, so we never claim "sent" before the
    // server says so. The dialog closes at once and the send runs in the background; the result toast
    // (success or failure) lands when it finishes.
    toast.info('Sending reminder emails…');
    setReminderOpen(false);
    setConfirmOpen(false);
    setConfirmText('');
    postReminder(false)
      .then(r => {
        if (r && (r.error || (r.failed || 0) > 0)) {
          toast.error(`Some reminders didn't send${r.failed ? ` (${r.failed} failed)` : ''}.${r.error ? ` ${r.error}` : ''}`, { autoClose: 10000 });
        } else {
          toast.success('Reminder emails sent.');
        }
      })
      .catch(err => toast.error(`Reminder send failed: ${err.message}`, { autoClose: 10000 }));
  }

  return (
    <>
      <TopBar />
      <div className="sp-page">
        <div className="sp-header">
          <div>
            <h1 className="sp-title">Steering</h1>
            <div className="sp-subtitle">Global progress across all sections</div>
          </div>
          <div className="sp-header-meta">
            <LastRefreshed at={lastRefreshedAt} freshness={freshness} onRefresh={silentRefetch} />
            {(displayData?.periods || []).length > 0 && (
              <label className="sp-period-switch">
                <span className="sp-period-switch-label">Cycle:</span>
                <select
                  className="sp-period-select"
                  value={selectedPeriod || displayData?.resolvedPeriod || ''}
                  onChange={(e) => setSelectedPeriod(e.target.value || null)}
                >
                  {displayData.periods.map(p => (
                    <option key={p.period} value={p.period}>{p.label || p.period}</option>
                  ))}
                </select>
              </label>
            )}
          </div>
        </div>

        <div className="sp-top-card">
          <div className="sp-filters">
            <div className="sp-filter-card">
              <button
                className={`sp-tab-btn ${tab === 'manager' ? 'active' : ''}`}
                onClick={() => setTab('manager')}
              >Manager</button>
              <button
                className={`sp-tab-btn ${tab === 'partner' ? 'active' : ''}`}
                onClick={() => setTab('partner')}
              >Partner</button>
            </div>
            <div className="sp-filter-card">
              <label>Office</label>
              <select value={officeFilter} onChange={e => setOfficeFilter(e.target.value)}>
                <option value="">All Offices</option>
                {offices.map(o => <option key={o} value={o}>{o}</option>)}
              </select>
            </div>
            <div className="sp-filter-card">
              <label>Level</label>
              <select value={levelFilter} onChange={e => setLevelFilter(e.target.value)}>
                <option value="">All Levels</option>
                {levels.map(l => <option key={l} value={l}>{l}</option>)}
              </select>
            </div>
            <div className="sp-filter-card">
              <label>Department</label>
              <select value={departmentFilter} onChange={e => setDepartmentFilter(e.target.value)}>
                <option value="">All Departments</option>
                {departments.map(d => <option key={d} value={d}>{d}</option>)}
              </select>
            </div>
          </div>

          <div className="sp-charts">
            {displayLoading ? <div className="sp-chart-loading"><i className="fas fa-spinner fa-spin" aria-hidden="true" /> Loading…</div>
             : (error && !displayData) ? <div className="sp-chart-error">Error: {error}</div>
             : typeAgg.length === 0 ? <div className="sp-chart-loading">No data</div>
             : typeAgg.map(([lookupType, agg]) => {
                const remaining = Math.max(0, agg.items - agg.completed);
                const dim = activeFilters.size > 0 && !activeFilters.has(lookupType);
                return (
                  <div
                    key={lookupType}
                    className={`sp-chart ${dim ? 'sp-chart-dim' : ''} ${activeFilters.has(lookupType) ? 'sp-chart-active' : ''}`}
                    onClick={() => toggleTypeFilter(lookupType)}
                  >
                    <div className="sp-chart-title">{agg.label}</div>
                    <div className="sp-chart-canvas-wrap">
                      <Doughnut
                        data={{
                          labels: ['Complete', 'Remaining'],
                          datasets: [{
                            data: [agg.completed, remaining],
                            backgroundColor: ['#4caf50', '#ddd'],
                            borderWidth: 0,
                          }],
                        }}
                        options={{
                          plugins: { legend: { display: false } },
                          cutout: '65%',
                          responsive: true,
                          maintainAspectRatio: true,
                          events: [],
                        }}
                      />
                    </div>
                    <div className="sp-chart-counts">{agg.completed} / {agg.items}</div>
                  </div>
                );
              })
            }
          </div>
        </div>

        <div className="sp-bottom-card">
          <div className="sp-progress-bar">
            <div
              className="sp-progress-fill"
              style={{
                width: `${Math.min(100, Math.max(0, overallPct))}%`,
                background: overallPct >= 100 ? '#1a7a4a'
                          : overallPct >= 50  ? '#e6a817'
                          : '#c0392b',
              }}
            />
            <div className="sp-progress-label">
              {overallPct}% ({totalCompleted} of {totalItems} items completed)
            </div>
          </div>

          <button className="sp-email-btn" onClick={openReminderModal}>
            <i className="fas fa-envelope" /> Send Email to Incomplete
          </button>

          <ReminderModal
            open={reminderOpen}
            onClose={() => setReminderOpen(false)}
            role={tab}
            typeAgg={typeAgg}
            officeFilter={officeFilter}
            levelFilter={levelFilter}
            departmentFilter={departmentFilter}
            staffPool={scoped}
            callerEmail={callerEmail}
            diPublicUrl={diPublicUrl}
            period={selectedPeriod || displayData?.resolvedPeriod || null}
            sectionMeta={displayData?.sectionMeta}
            sendingTest={sendingTest}
            testResult={testResult}
            realResult={realResult}
            confirmOpen={confirmOpen}
            confirmText={confirmText}
            setConfirmOpen={setConfirmOpen}
            setConfirmText={setConfirmText}
            subjectText={subjectText}
            introText={introText}
            closingText={closingText}
            signatureText={signatureText}
            setSubjectText={setSubjectText}
            setIntroText={setIntroText}
            setClosingText={setClosingText}
            setSignatureText={setSignatureText}
            includedSections={includedSections}
            setIncludedSections={setIncludedSections}
            excludedEmails={excludedEmails}
            setExcludedEmails={setExcludedEmails}
            fromKeith={fromKeith}
            setFromKeith={setFromKeith}
            emailMode={emailMode}
            onModeChange={applyEmailMode}
            onSendTest={handleSendTest}
            onSendReal={handleSendReal}
          />

          <div className="sp-tables">
            <div className="sp-table-col">
              <h3>Incomplete</h3>
              <StaffTable
                rows={incompleteRows}
                activeFilters={activeFilters}
                expanded={expandedIn}
                onToggleExpand={toggleRow(setExpandedIn)}
                tableKind="incomplete"
                loading={displayLoading}
              />
            </div>
            <div className="sp-table-col">
              <h3>Complete</h3>
              <StaffTable
                rows={completeRows}
                activeFilters={activeFilters}
                expanded={expandedDone}
                onToggleExpand={toggleRow(setExpandedDone)}
                tableKind="complete"
                loading={displayLoading}
              />
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
